import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { JdcManagerService } from '../jdc-manager.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-jdc-channel-modal',
    templateUrl: './jdc-channel-modal.component.html',
    styleUrls: ['./jdc-channel-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JdcChannelModalComponent implements OnChanges, OnDestroy {
  @Input() mode: 'new' | 'edit' = 'new';
  @Input() channelData: any;
  @Input() clientKey: any;
  @Input() channelList: any[] = [];

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  channelModalMinimise = false;
  channelForm: any = { channel_key: null, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };
  channelStatusList = [
    { name: 'Active', value: 'A' },
    { name: 'Inactive', value: 'I' },
  ];
  channelSaveLoading = false;

  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = { title: '', message: '' };
  private confirmCallback: (() => void) | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    if (this.channelData && this.mode === 'edit') {
      this.channelForm = {
        channel_key: this.channelData.channel_key,
        channel_name: this.channelData.channel_name,
        channel_desc: this.channelData.channel_desc,
        channel_priority: this.channelData.channel_priority,
        channel_status: this.channelData.channel_status || 'A'
      };
    } else {
      this.channelForm = { channel_key: null, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };
    }
    this.channelModalMinimise = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  clearChannelForm(): void {
    this.channelForm = { channel_key: null, channel_name: '', channel_desc: '', channel_priority: 1, channel_status: 'A' };
    this.cdr.detectChanges();
  }

  confirmRemoveChannel(channelKey: any): void {
    this.confirmModalConfig = {
      title: 'Remove Channel',
      message: `Are you sure you want to remove this Channel ${channelKey}?`,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
    };
    this.confirmCallback = () => {
      this.removeChanel(channelKey);
    };
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  executeConfirm(): void {
    this.showConfirmModal = false;
    if (this.confirmCallback) { this.confirmCallback(); }
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  cancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  saveChannelModal(): void {
    if (!this.channelForm.channel_name) {
      this.gtwSnackbarService.openSnackBar('Channel Name is required.', 'gtw-snackbar--red');
      return;
    }
    if (this.mode !== 'edit') {
      const exists = this.channelList.some((c: any) =>
        (c.channel_name || '').toLowerCase() === (this.channelForm.channel_name || '').toLowerCase()
      );
      if (exists) {
        this.gtwSnackbarService.openSnackBar(`Channel '${this.channelForm.channel_name}' already exists!`, 'gtw-snackbar--red');
        return;
      }
    }
    this.channelSaveLoading = true;
    const payload = [{
      CHANNEL_NAME: this.channelForm.channel_name,
      CHANNEL_DESC: this.channelForm.channel_desc || '',
      CHANNEL_PRIORITY: this.channelForm.channel_priority || 1,
      CHANNEL_STATUS: this.channelForm.channel_status || 'A',
      CLIENT_KEY: this.clientKey,
      ...(this.mode === 'edit' ? { CHANNEL_KEY: this.channelForm.channel_key } : {})
    }];
    const actionCode = this.mode === 'edit' ? 'h29d87' : 'nhuasw';
    this.jdcManagerService.saveJsonAction(actionCode, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.channelSaveLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              `Channel ${this.channelForm.channel_name} ${this.mode === 'edit' ? 'edited' : 'saved'} successfully.`,
              'gtw-snackbar--green'
            );
            this.saved.emit();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem saving channel.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.channelSaveLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem saving channel.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  removeChanel(channelKey: number): void {
    this.jdcManagerService.saveJsonAction('e3x0h6', [{ CHANNEL_KEY: channelKey }])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Channel removed successfully.', 'gtw-snackbar--green');
            this.saved.emit();
          } else {
            this.gtwSnackbarService.openSnackBar('Channel is in use and cannot be removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Channel is in use and cannot be removed.', 'gtw-snackbar--red')
      });
  }
}
